const Patents = () => {
  const patents = [
    {
      type: 'Patent',
      title: 'Optical Sensing Based Water Meter',
      number: 'Patent No. 10-2023-0001234',
      date: '2023.03.15',
      description: 'Non-contact water measurement technology using optical sensors',
      icon: 'ri-lightbulb-line',
      color: 'from-teal-500 to-teal-600',
    },
    {
      type: 'Patent',
      title: 'IoT Integrated Smart Metering System',
      number: 'Patent No. 10-2023-0005678',
      date: '2023.06.20',
      description: 'Real-time data transmission and remote monitoring system',
      icon: 'ri-wifi-line',
      color: 'from-teal-600 to-cyan-600',
    },
    {
      type: 'Patent Pending',
      title: 'Fraud Prevention Algorithm',
      number: 'Application No. 10-2024-0001111',
      date: '2024.01.10',
      description: 'AI-based anomaly pattern detection and automatic alert system',
      icon: 'ri-shield-check-line',
      color: 'from-cyan-500 to-cyan-600',
    },
    {
      type: 'Certification',
      title: 'KC Certification (National Integrated)',
      number: 'KC-REM-ABC12345',
      date: '2023.08.30',
      description: 'Electrical Appliances and Consumer Products Safety Control Act compliance certification',
      icon: 'ri-award-line',
      color: 'from-cyan-600 to-teal-700',
    },
  ];

  const certifications = [
    {
      name: 'ISO 9001',
      description: 'Quality Management System',
      icon: 'ri-medal-line',
    },
    {
      name: 'ISO 14001',
      description: 'Environmental Management System',
      icon: 'ri-leaf-line',
    },
    {
      name: 'OIML R49',
      description: 'International Legal Metrology',
      icon: 'ri-global-line',
    },
    {
      name: 'CE Mark',
      description: 'European Safety Standards',
      icon: 'ri-checkbox-circle-line',
    },
  ];

  const techAdvantages = [
    {
      title: 'Technical Superiority',
      items: [
        'First optical sensing technology in Korea',
        'Over 99.9% accuracy guaranteed',
        'Long-term use over 10 years possible',
      ],
    },
    {
      title: 'Competitiveness',
      items: [
        '30% price competitiveness vs existing products',
        '0% lead content eco-friendly material',
        'Real-time data management system',
      ],
    },
    {
      title: 'Scalability',
      items: [
        'Applicable to gas/electric meters',
        'Smart city platform integration',
        'Big data analysis capability',
      ],
    },
  ];

  return (
    <section id="patents" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <div className="inline-block px-5 py-2 bg-teal-50 border border-teal-200 rounded-full mb-4">
            <span className="text-teal-600 text-xs md:text-sm font-bold uppercase tracking-wider">Patents &amp; Certifications</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-gray-900 mb-3 md:mb-4">
            Patents &amp; Technical Certifications
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto font-medium">
            Demonstrating product excellence through proprietary technology and domestic/international certifications
          </p>
        </div>

        {/* Patent List */}
        <div className="mb-12 md:mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            {patents.map((patent, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl md:rounded-2xl p-5 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 group"
              >
                <div className="flex items-start gap-4 md:gap-6">
                  <div
                    className={`w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br ${patent.color} rounded-lg md:rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <i className={`${patent.icon} text-2xl md:text-3xl text-white`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="inline-block px-2 md:px-3 py-0.5 md:py-1 bg-teal-100 text-teal-700 rounded-full text-[10px] md:text-xs font-bold mb-2 md:mb-3">
                      {patent.type}
                    </div>
                    <h3 className="text-base md:text-xl font-bold text-gray-900 mb-1 md:mb-2">
                      {patent.title}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-600 mb-1 md:mb-2 truncate">
                      {patent.number}
                    </p>
                    <p className="text-xs md:text-sm text-gray-500 mb-2 md:mb-3">
                      Registration Date: {patent.date}
                    </p>
                    <p className="text-xs md:text-sm text-gray-700 leading-relaxed">
                      {patent.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Certification Status */}
        <div className="mb-12 md:mb-20">
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            Certification Status
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {certifications.map((cert, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-4 md:p-6 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200 text-center group"
              >
                <div className="w-12 md:w-16 h-12 md:h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${cert.icon} text-xl md:text-2xl text-white`}></i>
                </div>
                <h4 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">
                  {cert.name}
                </h4>
                <p className="text-[10px] md:text-sm text-gray-600">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Technical Advantages */}
        <div>
          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">
            Technical Advantages &amp; Competitiveness
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
            {techAdvantages.map((advantage, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl md:rounded-2xl p-5 md:p-8 border border-teal-100"
              >
                <h4 className="text-base md:text-xl font-bold text-gray-900 mb-4 md:mb-6 pb-3 md:pb-4 border-b-2 border-teal-200">
                  {advantage.title}
                </h4>
                <ul className="space-y-3 md:space-y-4">
                  {advantage.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 md:gap-3">
                      <div className="w-5 md:w-6 h-5 md:h-6 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-white text-xs md:text-sm"></i>
                      </div>
                      <span className="text-xs md:text-sm text-gray-700 leading-relaxed">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Patents;
